/**
 * Pure transforms from Claude Code transcript JSONL records into compact
 * `PtyChatEventPayload`s for the experimental chat PTY proxy.
 *
 * Deliberately separate from `record-mapper.ts`: that module's output must
 * stay in SDK parity (its contract is test-pinned), while this one is a lossy,
 * chat-shaped projection — it keeps user prompts (which the harness mapper
 * drops), truncates aggressively, and filters transcript noise.
 *
 * Transcript files are a trusted on-disk artifact written by the `claude`
 * CLI, so parsing their records is allowed — unlike raw PTY stdout, which is
 * never parsed.
 */

import type { PtyChatEventPayload } from "@project/shared";
import type { HarnessUserQuestion } from "../types.js";
import { parseUserQuestions } from "./pty-support.js";

// Headroom under the wire-schema caps (16_384) so a truncation ellipsis can
// never push a payload over the Zod limit server-side. Generic tool previews
// stay small on purpose — they are collapsed detail in the chat stream, and the
// server ring holds 500 events per session. AskUserQuestion is the exception:
// the web lifts it into an interactive card the human has to READ, so it gets
// the full text budget.
const TEXT_MAX = 16_000;
const TOOL_INPUT_MAX = 1_900;
const TOOL_OUTPUT_MAX = 1_900;
const QUESTION_INPUT_MAX = 16_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

function stringField(record: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value;
  }
  return undefined;
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/**
 * Serialize AskUserQuestion questions so the payload BOTH fits the wire cap
 * and stays parseable JSON — the web lifts it into an interactive question
 * card (`parseAskUserQuestion` in `ptyChatToolCalls.ts`) and silently falls
 * back to a plain tool group when parsing fails.
 *
 * The budget here is `QUESTION_INPUT_MAX`, not the generic `TOOL_INPUT_MAX`:
 * a human reads these option descriptions to pick an answer, so cutting them
 * short defeats the card. Real question sets fit whole. The degradation rungs
 * only exist for pathological payloads: the projection already drops unrendered
 * fields (e.g. option `preview` blocks, which alone can blow any budget), then
 * descriptions are shortened progressively and finally dropped, so the labels
 * and questions survive as parseable JSON either way.
 */
export function compactQuestionsJson(questions: HarnessUserQuestion[]): string {
  const serialize = (qs: HarnessUserQuestion[]): string => JSON.stringify({ questions: qs });
  const withDescriptions = (max: number): HarnessUserQuestion[] =>
    questions.map((q) => ({
      ...q,
      options: q.options.map((o) => ({ ...o, description: truncate(o.description, max) })),
    }));
  const full = serialize(questions);
  if (full.length <= QUESTION_INPUT_MAX) return full;
  for (const max of [2_000, 600, 80, 0]) {
    const shortened = serialize(withDescriptions(max));
    if (shortened.length <= QUESTION_INPUT_MAX) return shortened;
  }
  return serialize(withDescriptions(0)).slice(0, QUESTION_INPUT_MAX);
}

/** Compact tool_use `input` preview. AskUserQuestion routes through the
 * parseability-preserving projection; everything else is a blind slice. */
function compactToolInput(name: string, input: unknown): string {
  if (name === "AskUserQuestion" && isRecord(input)) {
    const questions = parseUserQuestions(input);
    if (questions.length > 0) return compactQuestionsJson(questions);
  }
  return JSON.stringify(input ?? {}).slice(0, TOOL_INPUT_MAX);
}

/** The CLI's synthetic background-task completion turn: a `run_in_background`
 *  command (or a backgrounded subagent) finishing re-injects one of these as a
 *  user message. */
const TASK_NOTIFICATION_PREFIX = "<task-notification>";

/**
 * Harness-injected user turns that are plumbing, not conversation, and must
 * never render as a user-sent chat bubble:
 * - `<command-name>` / `<local-command-…>` — local slash-command wrappers.
 * - `<task-notification>` — see above; without this filter it shows up as if
 *   the human sent it.
 */
function isNonConversationText(text: string): boolean {
  const trimmed = text.trimStart();
  return (
    trimmed.startsWith("<command-name>") ||
    trimmed.startsWith("<local-command-") ||
    trimmed.startsWith(TASK_NOTIFICATION_PREFIX)
  );
}

/**
 * Flatten a user record's message content to plain text. Returns undefined
 * when the record carries no prompt text (tool results, unknown shapes).
 */
function userRecordText(record: Record<string, unknown>): string | undefined {
  const message = record.message;
  if (!isRecord(message)) return undefined;
  const content = message.content;
  if (typeof content === "string") return content;
  if (!isUnknownArray(content)) return undefined;
  // Tool results ride user-role records; they are outputs, not prompts.
  if (content.some((b) => isRecord(b) && b.type === "tool_result")) return undefined;
  return content
    .filter((b): b is Record<string, unknown> => isRecord(b) && b.type === "text")
    .map((b) => (typeof b.text === "string" ? b.text : ""))
    .filter((t) => t.length > 0)
    .join("\n");
}

/**
 * Is this transcript record the CLI's background-task completion turn?
 *
 * Shared with the chat filter above rather than re-derived, so the two can
 * never drift. The runner counts these as the completion half of its
 * outstanding-background-work tracking (`runner/background-work.ts`) — that is
 * what keeps the workspace activity clock fresh while a backgrounded gate runs
 * and the agent itself sits idle.
 */
export function isBackgroundTaskNotificationRecord(raw: unknown): boolean {
  if (!isRecord(raw) || raw.type !== "user") return false;
  if (raw.isSidechain === true || raw.isMeta === true) return false;
  const text = userRecordText(raw);
  return text !== undefined && text.trimStart().startsWith(TASK_NOTIFICATION_PREFIX);
}

function mapSystem(record: Record<string, unknown>): PtyChatEventPayload[] {
  if (record.subtype === "init") {
    const event: PtyChatEventPayload = {
      kind: "init",
      model: stringField(record, "model") ?? "",
    };
    const sessionId = stringField(record, "session_id", "sessionId");
    if (sessionId !== undefined) event.claudeSessionId = sessionId;
    return [event];
  }
  // The interactive CLI marks a completed turn with a `turn_duration` system
  // record, not a `result` record — same mapping as record-mapper.ts.
  if (record.subtype === "turn_duration") return [{ kind: "turn_end" }];
  return [];
}

function mapAssistant(record: Record<string, unknown>): PtyChatEventPayload[] {
  const message = record.message;
  if (!isRecord(message)) return [];
  const content: unknown[] = isUnknownArray(message.content) ? message.content : [];
  const events: PtyChatEventPayload[] = [];
  for (const raw of content) {
    if (!isRecord(raw)) continue;
    if (raw.type === "text") {
      const text = stringField(raw, "text");
      if (text && text.length > 0) {
        events.push({ kind: "assistant_text", text: truncate(text, TEXT_MAX) });
      }
    } else if (raw.type === "tool_use") {
      const name = stringField(raw, "name");
      if (name) {
        const input = "input" in raw ? raw.input : undefined;
        const event: PtyChatEventPayload = {
          kind: "tool_use",
          name: truncate(name, 200),
          input: compactToolInput(name, input),
        };
        const id = stringField(raw, "id");
        if (id !== undefined) event.id = id;
        events.push(event);
      }
    }
  }
  return events;
}

/** Flatten a tool_result block's content (string or text-block array) to text. */
function toolResultText(block: Record<string, unknown>): string {
  const content = block.content;
  if (typeof content === "string") return content;
  if (isUnknownArray(content)) {
    return content
      .filter((b): b is Record<string, unknown> => isRecord(b) && b.type === "text")
      .map((b) => (typeof b.text === "string" ? b.text : ""))
      .filter((t) => t.length > 0)
      .join("\n");
  }
  return "";
}

function mapToolResults(content: unknown[]): PtyChatEventPayload[] {
  const events: PtyChatEventPayload[] = [];
  for (const raw of content) {
    if (!isRecord(raw) || raw.type !== "tool_result") continue;
    const event: PtyChatEventPayload = {
      kind: "tool_result",
      output: truncate(toolResultText(raw), TOOL_OUTPUT_MAX),
      isError: raw.is_error === true,
    };
    const toolUseId = stringField(raw, "tool_use_id");
    if (toolUseId !== undefined) event.toolUseId = toolUseId;
    events.push(event);
  }
  return events;
}

function mapUser(record: Record<string, unknown>): PtyChatEventPayload[] {
  const message = record.message;
  if (!isRecord(message)) return [];
  const content = message.content;

  if (isUnknownArray(content) && content.some((b) => isRecord(b) && b.type === "tool_result")) {
    return mapToolResults(content);
  }
  const text = userRecordText(record);
  if (text === undefined) return [];

  const trimmed = text.trim();
  if (trimmed.length === 0 || isNonConversationText(trimmed)) return [];
  return [{ kind: "user_text", text: truncate(trimmed, TEXT_MAX) }];
}

/**
 * Map one parsed transcript record to zero-or-more chat events (a record can
 * hold several content blocks). Unknown/noise records map to `[]`.
 */
export function mapChatRecords(raw: unknown): PtyChatEventPayload[] {
  if (!isRecord(raw)) return [];
  // Subagent (sidechain) traffic and meta records are transcript noise for a
  // chat surface — the main-thread records already narrate the turn.
  if (raw.isSidechain === true || raw.isMeta === true) return [];
  switch (raw.type) {
    case "system":
      return mapSystem(raw);
    case "assistant":
      return mapAssistant(raw);
    case "user":
      return mapUser(raw);
    case "result":
      return [{ kind: "turn_end" }];
    default:
      return [];
  }
}
